/**
 * <tecton-reveal> — scroll-triggered entrance.
 *
 * Liquid replacement for Reveal.tsx. Behaviour is matched to the React
 * component: threshold 0.1, rootMargin '0px 0px -30px 0px', unobserve after
 * the first intersection, and immediate reveal when the user prefers reduced
 * motion (which also guards hidden tabs and prerenders, where a scroll
 * trigger would never fire and the section would ship blank).
 *
 * Usage:
 *   <tecton-reveal>…</tecton-reveal>
 *   <tecton-reveal data-direction="left" data-delay="200" data-distance="40">…
 *
 * Attributes (all optional):
 *   data-direction  up | down | left | right | none   default: up
 *   data-distance   px travel                          default: 24
 *   data-delay      ms                                 default: 0
 *   data-duration   ms                                 default: 800
 */

const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// One observer for every instance on the page — cheaper than one each, and the
// options are identical across the theme.
let sharedObserver = null;

function getObserver() {
  if (sharedObserver) return sharedObserver;

  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.reveal();
        sharedObserver.unobserve(entry.target);
      }
    },
    { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
  );

  return sharedObserver;
}

class TectonReveal extends HTMLElement {
  connectedCallback() {
    // Reflect direction so CSS can pick the right transform.
    if (!this.dataset.direction) this.dataset.direction = 'up';

    const { distance, delay, duration } = this.dataset;
    if (distance) this.style.setProperty('--reveal-distance', `${distance}px`);
    if (delay) this.style.setProperty('--reveal-delay', `${delay}ms`);
    if (duration) this.style.setProperty('--reveal-duration', `${duration}ms`);

    if (window.matchMedia(MOTION_QUERY).matches) {
      this.reveal();
      return;
    }

    // In the theme editor, sections are injected after load and may already be
    // scrolled past — reveal immediately rather than waiting for a scroll that
    // will not come.
    if (window.Shopify?.designMode) {
      this.reveal();
      return;
    }

    getObserver().observe(this);
  }

  disconnectedCallback() {
    sharedObserver?.unobserve(this);
  }

  reveal() {
    this.dataset.revealed = '';
    // Drop the compositor hint once the transition has had time to finish.
    this.addEventListener(
      'transitionend',
      () => {
        this.style.willChange = 'auto';
      },
      { once: true }
    );
  }
}

if (!customElements.get('tecton-reveal')) {
  customElements.define('tecton-reveal', TectonReveal);
}
